import React, { useState, useEffect, useCallback } from "react";
import "../../styles/components/slidePuzzle/SlidePuzzle.scss";

export default function SlidePuzzle({ img, level, onSolved }) {
  const [grid, setGrid] = useState([]);
  const [isSolved, setIsSolved] = useState(false);

  const size = level === 1 ? 3 : level === 2 ? 4 : 5;

  const initPuzzle = useCallback(() => {
    const totalTiles = size * size;
    let initialGrid = Array.from({ length: totalTiles - 1 }, (_, i) => i + 1);
    initialGrid.push(null);

    let currentGrid = [...initialGrid];
    let emptyIdx = totalTiles - 1;

    const moveCount = size * size * 20;
    for (let i = 0; i < moveCount; i++) {
      const neighbors = [];
      const row = Math.floor(emptyIdx / size);
      const col = emptyIdx % size;

      if (row > 0) neighbors.push(emptyIdx - size);
      if (row < size - 1) neighbors.push(emptyIdx + size);
      if (col > 0) neighbors.push(emptyIdx - 1);
      if (col < size - 1) neighbors.push(emptyIdx + 1);

      const randomNeighbor =
        neighbors[Math.floor(Math.random() * neighbors.length)];
      [currentGrid[emptyIdx], currentGrid[randomNeighbor]] = [
        currentGrid[randomNeighbor],
        currentGrid[emptyIdx],
      ];
      emptyIdx = randomNeighbor;
    }

    setGrid(currentGrid);
    setIsSolved(false);
  }, [size]);

  useEffect(() => {
    initPuzzle();
  }, [initPuzzle, img]);

  const handleTileClick = (index) => {
    if (isSolved) return;

    const emptyIndex = grid.indexOf(null);
    const row = Math.floor(index / size);
    const col = index % size;
    const emptyRow = Math.floor(emptyIndex / size);
    const emptyCol = emptyIndex % size;

    const isAdjacent =
      (Math.abs(row - emptyRow) === 1 && col === emptyCol) ||
      (Math.abs(col - emptyCol) === 1 && row === emptyRow);

    if (isAdjacent) {
      const newGrid = [...grid];
      [newGrid[index], newGrid[emptyIndex]] = [
        newGrid[emptyIndex],
        newGrid[index],
      ];
      setGrid(newGrid);

      // 승리 체크
      const win = newGrid.every((tile, i) => {
        if (i === size * size - 1) return tile === null;
        return tile === i + 1;
      });
      if (win) {
        setIsSolved(true);
        if (onSolved) {
          setTimeout(() => {
            onSolved();
          }, 1000); // 1초 지연 후 모달 팝업
        }
      }
    }
  };

  // 퍼즐 판의 전체 타일 리스트 (마지막 조각 포함)
  const tileValues = Array.from({ length: size * size }, (_, i) => i + 1);

  return (
    <div className="slide-puzzle-container" style={{ "--grid-size": size }}>
      <div className={`slide-puzzle-grid ${isSolved ? "solved" : ""}`}>
        {tileValues.map((tileValue) => {
          // 마지막 조각(size * size)은 퍼즐이 풀렸을 때만 null 자리에 렌더링
          const isLastTile = tileValue === size * size;
          const index = isLastTile
            ? grid.indexOf(null)
            : grid.indexOf(tileValue);

          if (index === -1 || (isLastTile && !isSolved)) return null;

          const row = Math.floor(index / size);
          const col = index % size;

          // 타일의 원래 위치(정답 위치) 계산
          const originalIdx = tileValue - 1;
          const origRow = Math.floor(originalIdx / size);
          const origCol = originalIdx % size;

          const posX = (origCol / (size - 1)) * 100;
          const posY = (origRow / (size - 1)) * 100;

          return (
            <div
              key={tileValue}
              className={`tile ${isLastTile ? "last-tile" : ""}`}
              onClick={() => !isLastTile && handleTileClick(index)}
              style={{
                position: "absolute",
                width: `calc(100% / ${size})`,
                height: `calc(100% / ${size})`,
                top: `${(row * 100) / size}%`,
                left: `${(col * 100) / size}%`,
                backgroundImage: `url(${img})`,
                backgroundPosition: `${posX}% ${posY}%`,
                backgroundSize: `${size * 100}% ${size * 100}%`,
                transition: "all 0.2s ease-out",
              }}
            >
              {!isLastTile && <span className="tile-number">{tileValue}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
